// Compares the report in the editor with an AI proposal, as a list of human-sized changes the builder can
// accept one by one (apply selected) or all together.

const COLLECTIONS = [
  { path: 'params', label: 'Filter', key: (x) => x.name },
  { path: 'datasets', label: 'Dataset', key: (x) => x.name },
  { path: 'ui.sections', label: 'Section', key: (x) => x.id || `${x.type}:${x.title || ''}` },
  { path: 'info', label: 'Calculation detail', key: (x) => x.label },
  { path: 'exports', label: 'Export', key: (x) => `${x.name}|${x.type}|${x.dataset}` },
  { path: 'prints', label: 'Print layout', key: (x) => x.name },
  { path: 'drilldowns', label: 'Underlying-data view', key: (x) => x.id }
]
const SCALARS = [
  ['name', 'Name'], ['description', 'Description'], ['category', 'Category'], ['tags', 'Tags'],
  ['order', 'Order'], ['urlFilters', 'Keep filters in the URL'], ['key', 'Report key']
]

const get = (o, path) => path.split('.').reduce((a, k) => (a == null ? a : a[k]), o)
function set (o, path, v) {
  const parts = path.split('.')
  let t = o
  for (let i = 0; i < parts.length - 1; i++) { if (t[parts[i]] == null) t[parts[i]] = {}; t = t[parts[i]] }
  t[parts[parts.length - 1]] = v
}

// key-order independent comparison
function stable (v) {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(',')}}`
  return JSON.stringify(v)
}
const same = (a, b) => stable(a === undefined ? null : a) === stable(b === undefined ? null : b)

/** Text shown in the before/after panes. Datasets show their SQL (readable), everything else pretty JSON. */
export function renderItem (item, path) {
  if (item === undefined || item === null) return ''
  if (path === 'datasets') return `${item.sql}${item.processing ? `\n\n/* processing */\n${item.processing}` : ''}`
  if (typeof item === 'string') return item
  return JSON.stringify(item, null, 2)
}

/** @returns [{ id, group, title, kind: 'added'|'removed'|'changed'|'reordered', path, key?, before, after }] */
export function diffTemplates (cur, next) {
  const out = []
  for (const [field, label] of SCALARS) {
    if (!same(cur[field], next[field])) {
      out.push({ id: `s:${field}`, group: 'Report details', title: label, kind: 'changed', scalar: field, before: renderItem(cur[field]), after: renderItem(next[field]) })
    }
  }
  for (const c of COLLECTIONS) {
    const a = get(cur, c.path) || []
    const b = get(next, c.path) || []
    const am = new Map(a.map((x) => [c.key(x), x]))
    const bm = new Map(b.map((x) => [c.key(x), x]))
    for (const [k, x] of bm) {
      if (!am.has(k)) out.push({ id: `${c.path}:+:${k}`, group: c.label + 's', title: `${c.label}: ${k}`, kind: 'added', path: c.path, key: k, before: '', after: renderItem(x, c.path) })
      else if (!same(am.get(k), x)) out.push({ id: `${c.path}:~:${k}`, group: c.label + 's', title: `${c.label}: ${k}`, kind: 'changed', path: c.path, key: k, before: renderItem(am.get(k), c.path), after: renderItem(x, c.path) })
    }
    for (const [k, x] of am) {
      if (!bm.has(k)) out.push({ id: `${c.path}:-:${k}`, group: c.label + 's', title: `${c.label}: ${k}`, kind: 'removed', path: c.path, key: k, before: renderItem(x, c.path), after: '' })
    }
    const common = [...bm.keys()].filter((k) => am.has(k))
    const order = (map, keys) => keys.filter((k) => map.has(k)).join('|')
    if (common.length > 1 && order(am, [...am.keys()].filter((k) => bm.has(k))) !== order(bm, common)) {
      out.push({ id: `${c.path}:order`, group: c.label + 's', title: `Order of ${c.label.toLowerCase()}s`, kind: 'reordered', path: c.path, before: [...am.keys()].join('\n'), after: [...bm.keys()].join('\n'), order: [...bm.keys()] })
    }
  }
  if (!same(cur.tests, next.tests)) {
    out.push({ id: 'tests', group: 'Tests', title: 'Tests (fixtures, cases, reconciliations)', kind: 'changed', tests: true, before: renderItem(summaryOfTests(cur.tests)), after: renderItem(summaryOfTests(next.tests)) })
  }
  return out
}

function summaryOfTests (t) {
  if (!t) return null
  return {
    fixtureTables: Object.keys(t.fixtures || {}),
    cases: (t.cases || []).map((c) => c.name),
    reconciliations: (t.reconciliations || []).map((r) => r.name)
  }
}

/** Start from the current report and apply only the accepted changes. */
export function applyChanges (cur, next, changes, acceptedIds) {
  const out = JSON.parse(JSON.stringify(cur))
  const accepted = new Set(acceptedIds)
  for (const ch of changes) {
    if (!accepted.has(ch.id)) continue
    if (ch.scalar) { out[ch.scalar] = next[ch.scalar]; continue }
    if (ch.tests) { out.tests = JSON.parse(JSON.stringify(next.tests)); continue }
    const coll = COLLECTIONS.find((c) => c.path === ch.path)
    const nextList = get(next, ch.path) || []
    let list = get(out, ch.path) || []
    if (ch.kind === 'reordered') {
      const rank = new Map(ch.order.map((k, i) => [k, i]))
      list = [...list].sort((x, y) => (rank.has(coll.key(x)) ? rank.get(coll.key(x)) : 1e6) - (rank.has(coll.key(y)) ? rank.get(coll.key(y)) : 1e6))
    } else if (ch.kind === 'removed') {
      list = list.filter((x) => coll.key(x) !== ch.key)
    } else {
      const item = JSON.parse(JSON.stringify(nextList.find((x) => coll.key(x) === ch.key)))
      const i = list.findIndex((x) => coll.key(x) === ch.key)
      if (i >= 0) list = list.map((x, k) => (k === i ? item : x))
      else list = [...list, item]
    }
    set(out, ch.path, list)
  }
  return out
}
